import { useEffect, useLayoutEffect, useState, useRef } from 'react'

import { getFeatureGate } from '../features/feature-gate'
import type { FeatureGate } from '../types'

export const STATSIG_READY_EVENT = 'statsig:ready'

// Reads the (already SSR-safe, null-initialized) cache synchronously before
// paint on the client, so pages that branch rendering on a gate value don't
// flash the default variant and then remount into the real one.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect

/**
 * Hook reativo para feature gates do Statsig
 * Re-renderiza o componente quando o feature gate estiver disponível.
 *
 * @param gateName - Nome do feature gate
 * @returns FeatureGate | null
 *
 */
export function useStatsigFeatureGate(gateName: string): FeatureGate | null {
  // Always start with null to match SSR output and avoid hydration mismatch.
  // localStorage is only available on the client, so reading it in useState
  // initializer would cause server/client divergence (React error #418).
  const [gate, setGate] = useState<FeatureGate | null>(null)

  // Read from localStorage after mount (client-only), before paint
  useIsomorphicLayoutEffect(() => {
    const initialGate = getFeatureGate(gateName)

    if (initialGate?.value !== null && initialGate?.value !== undefined) {
      setGate(initialGate)
    }
  }, [gateName])

  useEffect(() => {
    if (gate?.value !== null && gate?.value !== undefined) {
      return undefined
    }

    const handleStatsigReady = () => {
      const updatedGate = getFeatureGate(gateName)

      if (updatedGate?.value !== null && updatedGate?.value !== undefined) {
        setGate(updatedGate)
      }
    }

    window.addEventListener(STATSIG_READY_EVENT, handleStatsigReady)

    const timeoutId = setTimeout(() => {
      const updatedGate = getFeatureGate(gateName)

      if (
        updatedGate?.value !== null &&
        updatedGate?.value !== undefined &&
        updatedGate?.value !== gate?.value
      ) {
        setGate(updatedGate)
      }
    }, 100)

    return () => {
      window.removeEventListener(STATSIG_READY_EVENT, handleStatsigReady)
      clearTimeout(timeoutId)
    }
  }, [gate, gateName])

  return gate
}

/**
 * Hook simplificado que retorna apenas o valor booleano do feature gate
 *
 * @param gateName - Nome do feature gate
 * @param defaultValue - Valor padrão enquanto não carregar (default: false)
 * @returns boolean
 *
 * @example
 * const isExperimentEnabled = useStatsigFeatureGateValue('experiment_name')
 */
export function useStatsigFeatureGateValue(
  gateName: string,
  defaultValue = false
): boolean {
  const gate = useStatsigFeatureGate(gateName)

  return gate?.value ?? defaultValue
}

/**
 * Hook especializado para exposição de experimentos (analytics).
 * Garante que o callback só é executado quando o valor REAL estiver disponível,
 * evitando enviar dados incorretos com valores default.
 *
 * @param gateName - Nome do feature gate
 * @param onExposure - Callback executado UMA VEZ quando o valor estiver disponível
 * @returns { value: boolean | null, isReady: boolean }
 *
 */
export function useStatsigExposure(
  gateName: string,
  onExposure: (value: boolean, gate: FeatureGate) => void
): { value: boolean | null; isReady: boolean } {
  const gate = useStatsigFeatureGate(gateName)
  const [hasDispatched, setHasDispatched] = useState(false)
  const onExposureRef = useRef(onExposure)

  useEffect(() => {
    onExposureRef.current = onExposure
  }, [onExposure])

  useEffect(() => {
    if (
      gate &&
      gate.value !== undefined &&
      gate.value !== null &&
      !hasDispatched
    ) {
      try {
        onExposureRef.current(gate.value, gate)
        setHasDispatched(true)
      } catch (error) {
        console.error('Error in useStatsigExposure callback:', error)
      }
    }
  }, [gate, hasDispatched])

  return {
    value: gate?.value ?? null,
    isReady: gate !== null && gate.value !== undefined && gate.value !== null,
  }
}
